import { Configuration, DefaultConfig, EventName, EventPostRequest, EventsApi } from "../../clients/slashid-api"
import { v4 as uuid } from "uuid"
import { BaseUser } from "../user.base"
import { PersonIdentifiedEvent, PersonLoggedOutEvent } from "../../utils/events"
import { SlashIDConfig, checkSlashIDOptions, createSlashIDConfig } from "../../domain/options"
import { AnalyticsOptions, AnalyticsTransport } from "./analytics.types"
import { createFetchTransport } from "./transport"

/**
 * Server-side rendering friendly analytics class.
 * Can be used to identify users and track virtual page views.
 */
export class BaseAnalytics {
    protected eventsAPI: EventsApi
    protected user?: BaseUser
    protected readonly config: SlashIDConfig
    protected correlationId: string
    protected transport: AnalyticsTransport

    constructor({ options, user, transport }: AnalyticsOptions) {
        if (options) {
            checkSlashIDOptions(options)
        }

        this.config = createSlashIDConfig(options ?? {})
        this.transport = transport ?? createFetchTransport(fetch, this.config)

        this.eventsAPI = new EventsApi(new Configuration({ ...DefaultConfig, basePath: this.config.baseURL }))
        this.user = user

        // how do we keep this in sync with the server side
        this.correlationId = this.createCorrelationID()
    }

    protected createCorrelationID() {
        return uuid()
    }

    protected createEventMetadata(
        { location, userAgent }: { location: string; userAgent: string } = { location: "/", userAgent: "" }
    ) {
        return {
            organization_id: this.config.oid ?? this.user?.oid ?? "",
            analytics_correlation_id: this.correlationId,
            window_location: location,
            user_agent: userAgent,
        }
    }

    protected sendEvent(payload: EventPostRequest) {
        this.transport.sendEvent(payload)
    }

    protected trackPersonIdentified({ user }: PersonIdentifiedEvent) {
        const personIdentifiedEvent = {
            event_name: EventName.PersonIdentifiedV1,
            person_id: user.ID,
        }

        const payload: EventPostRequest = {
            ...this.createEventMetadata(),
            event_data: personIdentifiedEvent,
        }

        this.sendEvent(payload)
    }

    protected trackPersonLoggedOut(e: PersonLoggedOutEvent) {
        const user = new BaseUser(e.token, this.config)

        const personLoggedOut = {
            event_name: EventName.PersonLoggedOutV1,
            person_id: user.ID,
        }

        const payload: EventPostRequest = {
            ...this.createEventMetadata(),
            event_data: personLoggedOut,
        }

        this.sendEvent(payload)
    }

    /**
     * Identifies a user for the purpose of user activity tracking. Where possible
     * user identification is done automatically.
     *
     * @param user The user being identified.
     */
    public identify(user: BaseUser) {
        this.user = user

        this.trackPersonIdentified({ user })
    }

    /**
     * Logs the user out from the point-of-view of the {@link Types.BaseAnalytics} class only,
     * this method does not have a side-effect which affects the logged in state of the user.
     *
     * After removing a stored token this method should be called to record the log out event.
     */
    public logout() {
        const user = this.user
        if (!user) {
            return
        }

        this.trackPersonLoggedOut({ token: user.token })
        this.user = undefined
    }

    /**
     * Tracks a virtual page view
     *
     * A virtual page view allows you to track a page view, even when a page is not physically
     * loaded in the browser. For example, when navigating using a client-side router in a
     * single page application, or navigating within a sub-section of a page (like a tabbed pane,
     * or navigation drawer) without changing the page itself.
     *
     * The exact meaning of a virtual page view will vary by implementation, it depends on your
     * routing paradigm and what you're trying to track. You will need to implement this event
     * in a way which makes sense for your product.
     *
     * @param options.url The URL of the page to track as the subject of the virtual page view.
     */
    public trackVirtualPageView(options: { url?: string; userAgent?: string }) {
        const { url = "" } = options

        const virtualPageLoadEvent = {
            event_name: EventName.VirtualPageLoadedV1,
            ...(this.user && { person_id: this.user.ID }),
        }

        const payload: EventPostRequest = {
            ...this.createEventMetadata({ location: url, userAgent: options.userAgent ?? "" }),
            event_data: virtualPageLoadEvent,
        }

        this.sendEvent(payload)
    }
}
